import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  Dropdown,
  DropdownButton,
  Form,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import {
  useDeleteProjectByIdMutation,
  useGetProjectsMutation,
} from 'redux/services/project';
import { useSelector } from 'react-redux';
import { navLinks } from '../../constants/paths';
import MoreVerticalIcon from 'assets/images/more-vertical.svg';
import moment from 'moment';
import toast from 'react-hot-toast';
import Modal from 'components/common/Modal/Modal';
import { Tooltip } from '@mui/material';
import { RootStates } from 'redux/store';
// import useElementSize from 'hooks/useElementSize';
import { useDispatch } from 'react-redux';
import {
  Project,
  saveProjects,
  setFetchProjectsInProgress,
} from 'redux/projectSlice';

import './Projects.scss';
import { Hidden } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { userRoles } from 'constants/constants';
import { Loader } from 'components/common/Loader';
import { CalendarMonth, LocationOn, Person } from '@mui/icons-material';

const Projects = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [getProjects, { isLoading: isProjectsLoading }] =
    useGetProjectsMutation();
  const fetchedProjects = useSelector(
    (state: RootStates) => state.project.projects,
  );
  const [deleteProjectById, { isLoading }] = useDeleteProjectByIdMutation();
  const [projects, setProjects] = useState<Project[]>(fetchedProjects);
  const [activeTab, setActiveTab] = useState('active');
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string>('');
  const dispatch = useDispatch();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const searchProjects = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const filteredProjects = useMemo(() => {
    return projects
      .filter((project) => {
        if (activeTab === 'active') {
          return project.isLaunched;
        } else if (activeTab === 'draft') {
          return !project.isLaunched;
        }

        return true;
      })
      .filter((project) => {
        const regEx = new RegExp(query, 'i');
        return (
          project.projectInfo.projectName.match(regEx) ||
          project.projectInfo.projectId.match(regEx)
        );
      });
  }, [activeTab, projects, query]);

  const deleteProject = async () => {
    const res: any = await deleteProjectById(deleteProjectId);
    if (res.data) {
      handleFetchProject();
      toast.success(t('success_deleted'));
    } else {
      toast.error(res.error.data.message);
    }
    handleClose();
  };

  const handleFetchProject = useCallback(async () => {
    dispatch(setFetchProjectsInProgress());
    const payload: any = await getProjects('');
    setProjects(payload.data || []);
    dispatch(saveProjects(payload.data || []));
  }, [dispatch, getProjects]);

  useEffect(() => {
    handleFetchProject();
  }, [handleFetchProject]);

  const getTabClickHandler = (tab: string) => () => {
    setActiveTab(tab);
  };

  const getProjectClickHandler = (project: Project) => () => {
    if (project.isLaunched) {
      navigate(`/project/${project.projectInfo.id}`);
    } else if (
      project?.role === userRoles.OWNER ||
      project?.role === userRoles.PROJECT_ADMIN
    ) {
      navigate(`/project/setup/${project.projectInfo.id}/1`);
    } else {
      toast.error(
        'Project is in draft state. You do not have permission to view it.',
      );
    }
  };

  return (
    <div className="projects d-flex flex-column h-100">
      {(isProjectsLoading || isLoading) && !filteredProjects.length && (
        <Loader />
      )}
      <div className="projects-list d-flex flex-column h-100">
        <div className="d-flex justify-content-between align-items-center page-title mb-3">
          <h2 className="mb-0">{t('header_projects')}</h2>
          <Hidden mdDown>
            <Form.Control
              type="search"
              placeholder={t('input_search_project_placeholder')}
              name="search"
              className="mx-3"
              onChange={searchProjects}
            />
          </Hidden>

          <ButtonGroup className="p-0">
            <Button
              className="shadow-none"
              variant={activeTab === 'all' ? 'primary' : 'outline-primary'}
              onClick={getTabClickHandler('all')}
            >
              {t('tab_all')}
            </Button>
            <Button
              className="shadow-none"
              variant={activeTab === 'active' ? 'primary' : 'outline-primary'}
              onClick={getTabClickHandler('active')}
            >
              {t('tab_active')}
            </Button>
            <Button
              className="shadow-none"
              variant={activeTab === 'draft' ? 'primary' : 'outline-primary'}
              onClick={getTabClickHandler('draft')}
            >
              {t('tab_draft')}
            </Button>
          </ButtonGroup>
        </div>
        <Hidden mdUp>
          <Form.Control
            type="search"
            placeholder={t('input_search_project_placeholder')}
            name="search"
            className="mb-2"
            onChange={searchProjects}
          />
        </Hidden>
        <Card className="border-0 flex-grow-1 overflow-auto">
          <Card.Body className="project-wrapper h-100 d-flex flex-column">
            <div className="step-wrapper">
              <div className="d-flex gap-3 flex-column flex-md-row flex-md-wrap h-100">
                {filteredProjects?.length > 0 ? (
                  filteredProjects.map((project: Project) => (
                    <div
                      key={project.projectInfo.id}
                      className="project p-0"
                      onClick={getProjectClickHandler(project)}
                    >
                      <Card className="project-details">
                        <Card.Body className="project-card">
                          <div className="d-flex justify-content-between align-items-center position-absolute status-container">
                            <span
                              className={`status ${
                                project.isLaunched ? 'active' : 'draft'
                              }`}
                            >
                              {project.isLaunched === true
                                ? t('tab_active')
                                : t('tab_draft')}
                            </span>
                            {(project?.role === userRoles.OWNER ||
                              project?.role === userRoles.PROJECT_ADMIN) && (
                              <DropdownButton
                                align="end"
                                variant="link"
                                onClick={(e) => e.stopPropagation()}
                                title={<img src={MoreVerticalIcon} alt="" />}
                              >
                                {project.isLaunched ? (
                                  <Link
                                    className="dropdown-item"
                                    to={`/project/setup/${project.projectInfo.id}/1`}
                                  >
                                    {t('edit')}
                                  </Link>
                                ) : (
                                  ''
                                )}

                                {project.role === userRoles.OWNER && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setDeleteProjectId(
                                        project.projectInfo!.id ?? '',
                                      );
                                      handleShow();
                                    }}
                                  >
                                    {t('text_delete')}
                                  </Dropdown.Item>
                                )}
                              </DropdownButton>
                            )}
                          </div>
                          <img
                            src={
                              project.projectInfo.projectImg ||
                              `https://ui-avatars.com/api/?size=80&color=fff&rounded=true&bold=true&background=random&name=${project.projectInfo.projectName}`
                            }
                            width="80"
                            height="80"
                            className="rounded-circle project-img"
                            alt=""
                          />
                          <div className="mb-1 mt-2">
                            <div className="d-flex align-items-center">
                              <h4 className="mb-0 me-2">
                                {project.projectInfo.projectName}
                              </h4>
                            </div>
                            <span className="project-id text-muted d-block">
                              {project.projectInfo.projectId}
                            </span>
                          </div>
                          <div className="divider" />
                          <div className="d-flex align-items-center text-muted">
                            <Person />
                            <span className="ms-2 text-capitalize">
                              {project.role.toLowerCase()}
                            </span>
                          </div>
                          <div className="d-flex align-items-center text-muted overflow-hidden my-1">
                            <LocationOn />
                            <span className="ms-2 text-truncate">
                              {project.projectInfo.location || 'Delhi'}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center text-muted">
                              <CalendarMonth />
                              <span className="ms-2">
                                {moment(project.created_at).format(
                                  'DD-MM-YYYY',
                                )}
                              </span>
                            </div>
                            <div className="d-flex align-items-center text-muted">
                              {project?.team
                                ?.filter(
                                  (t) => t.status.toLowerCase() === 'joined',
                                )
                                .slice(0, 4)
                                .map((team: any) => (
                                  <Tooltip
                                    key={team.email}
                                    title={team.email}
                                    placement="top"
                                    arrow
                                  >
                                    <img
                                      src={`https://ui-avatars.com/api/?size=24&color=fff&rounded=false&bold=true&background=random&name=${team.email}`}
                                      className="rounded-circle"
                                      alt=""
                                    />
                                  </Tooltip>
                                ))}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ))
                ) : (
                  <div className="d-flex flex-column align-items-center w-100 h-100 text-center">
                    <div className="project-placeholder w-100 flex-grow-1" />
                    <p className="font-bold mt-2 mb-0">
                      {t('projects_header_1', { type: activeTab })} <br />
                      {t('projects_header_2')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center mt-2">
              <Link className="btn btn-primary" to={navLinks.projectSetup}>
                {t('btn_setup_project')}
              </Link>
            </div>
          </Card.Body>
        </Card>
      </div>
      <Modal
        show={show}
        title={t('text_delete')}
        cancelBtnText={t('btn_cancel')}
        submitBtnText={t('btn_delete')}
        withIcon
        onClose={handleClose}
        onSubmit={deleteProject}
        disabled={isLoading}
      />
    </div>
  );
};

export default Projects;
